import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Square, Wifi, Clock, TrendingUp, Activity, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface PingResult {
  sequence: number;
  time: number;
  status: 'success' | 'timeout' | 'error';
  timestamp: Date;
}

interface PingStats {
  sent: number;
  received: number;
  lost: number;
  lossPercentage: number;
  minTime: number;
  maxTime: number;
  avgTime: number;
}

export const PingTestTool: React.FC = () => {
  const { t } = useTranslation();
  const [host, setHost] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<PingResult[]>([]);
  const [stats, setStats] = useState<PingStats | null>(null);
  const [error, setError] = useState('');
  const [pingCount, setPingCount] = useState(4);
  const [interval, setInterval] = useState(1000);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceRef = useRef(0);

  // Mock ping function for demonstration
  const mockPing = async (hostname: string): Promise<{ time: number; status: 'success' | 'timeout' | 'error' }> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 50 + Math.random() * 100));
    
    // Simulate occasional timeouts/errors (10% chance)
    const random = Math.random();
    if (random < 0.05) {
      return { time: 0, status: 'timeout' };
    } else if (random < 0.08) {
      return { time: 0, status: 'error' };
    }
    
    // Simulate realistic ping times (10-200ms)
    const baseTime = hostname.includes('localhost') || hostname.includes('127.0.0.1') ? 1 : 20;
    const time = Math.round(baseTime + Math.random() * 180);
    
    return { time, status: 'success' };
  };

  const validateHost = (hostname: string): boolean => {
    // IP address regex
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    // Domain name regex
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;
    
    return ipRegex.test(hostname) || domainRegex.test(hostname) || hostname === 'localhost';
  };

  const calculateStats = (pingResults: PingResult[]): PingStats => {
    const successfulPings = pingResults.filter(r => r.status === 'success');
    const times = successfulPings.map(r => r.time);
    
    return {
      sent: pingResults.length,
      received: successfulPings.length,
      lost: pingResults.length - successfulPings.length,
      lossPercentage: ((pingResults.length - successfulPings.length) / pingResults.length) * 100,
      minTime: times.length > 0 ? Math.min(...times) : 0,
      maxTime: times.length > 0 ? Math.max(...times) : 0,
      avgTime: times.length > 0 ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0
    };
  };

  const startPing = async () => {
    if (!host.trim()) {
      setError(t('tools.pingTest.enterHostError'));
      return;
    }

    if (!validateHost(host.trim())) {
      setError(t('tools.pingTest.invalidHostError'));
      return;
    }

    setIsRunning(true);
    setError('');
    setResults([]);
    setStats(null);
    sequenceRef.current = 0;

    const runPing = async () => {
      if (sequenceRef.current >= pingCount) {
        stopPing();
        return;
      }

      try {
        const result = await mockPing(host.trim());
        const pingResult: PingResult = {
          sequence: sequenceRef.current + 1,
          time: result.time,
          status: result.status,
          timestamp: new Date()
        };

        setResults(prev => {
          const newResults = [...prev, pingResult];
          setStats(calculateStats(newResults));
          return newResults;
        });

        sequenceRef.current++;

        if (sequenceRef.current < pingCount) {
          intervalRef.current = setTimeout(runPing, interval);
        } else {
          setIsRunning(false);
        }
      } catch (err) {
        setError(t('tools.pingTest.pingError'));
        setIsRunning(false);
      }
    };

    runPing();
  };

  const stopPing = () => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const clearResults = () => {
    setResults([]);
    setStats(null);
    setError('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'timeout': return 'text-yellow-600 dark:text-yellow-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✓';
      case 'timeout': return '⏱';
      case 'error': return '✗';
      default: return '?';
    }
  };

  const commonHosts = [
    { name: t('tools.pingTest.commonHosts.googleDns'), host: '8.8.8.8' },
    { name: t('tools.pingTest.commonHosts.cloudflareDns'), host: '1.1.1.1' },
    { name: t('tools.pingTest.commonHosts.google'), host: 'google.com' },
    { name: t('tools.pingTest.commonHosts.github'), host: 'github.com' },
    { name: t('tools.pingTest.commonHosts.localhost'), host: 'localhost' },
  ];

  return (
    <div className="space-y-6">
      {/* Ping Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Wifi className="w-5 h-5" />
            <span>{t('tools.pingTest.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  label={t('tools.pingTest.hostnameOrIp')}
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder={t('tools.pingTest.hostPlaceholder')}
                  onKeyPress={(e) => e.key === 'Enter' && !isRunning && startPing()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.pingTest.pingCount')}
                </label>
                <select
                  value={pingCount}
                  onChange={(e) => setPingCount(parseInt(e.target.value))}
                  disabled={isRunning}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value={4}>{t('tools.pingTest.pingOptions.4pings')}</option>
                  <option value={10}>{t('tools.pingTest.pingOptions.10pings')}</option>
                  <option value={20}>{t('tools.pingTest.pingOptions.20pings')}</option>
                  <option value={50}>{t('tools.pingTest.pingOptions.50pings')}</option>
                  <option value={-1}>{t('tools.pingTest.pingOptions.continuous')}</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Interval (ms)
                </label>
                <select
                  value={interval}
                  onChange={(e) => setInterval(parseInt(e.target.value))}
                  disabled={isRunning}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value={500}>500ms</option>
                  <option value={1000}>1 second</option>
                  <option value={2000}>2 seconds</option>
                  <option value={5000}>5 seconds</option>
                </select>
              </div>
              <div className="flex items-end">
                <div className="flex gap-2 w-full">
                  <Button
                    onClick={isRunning ? stopPing : startPing}
                    disabled={!host.trim()}
                    variant="primary"
                    className="flex-1"
                  >
                    {isRunning ? (
                      <>
                        <Square className="w-4 h-4 mr-2" />
                        {t('tools.pingTest.stop')}
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        {t('tools.pingTest.startPing')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={clearResults}
                    disabled={isRunning}
                    variant="outline"
                  >
                    {t('common.clear')}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Common Hosts */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.pingTest.quickTest')}:
              </label>
              <div className="flex flex-wrap gap-2">
                {commonHosts.map((item) => (
                  <Button
                    key={item.host}
                    onClick={() => setHost(item.host)}
                    disabled={isRunning}
                    variant="outline"
                    size="sm"
                  >
                    {item.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5" />
              <span>{t('tools.pingTest.pingStatistics')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.sent}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Sent</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.received}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Received</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {stats.lossPercentage.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Packet Loss</div>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.avgTime}ms
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Avg Time</div>
              </div>
              <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.minTime}-{stats.maxTime}ms
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Min-Max</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ping Results */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>{t('tools.pingTest.pingResultsFor')} {host}</span>
              {isRunning && (
                <div className="ml-auto flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>{t('tools.pingTest.running')}</span>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {results.map((result, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <span className={`text-lg ${getStatusColor(result.status)}`}>
                      {getStatusIcon(result.status)}
                    </span>
                    <span className="font-mono text-sm">
                      seq={result.sequence}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {result.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    {result.status === 'success' ? (
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-sm">
                          {result.time}ms
                        </span>
                      </div>
                    ) : (
                      <span className={`text-sm ${getStatusColor(result.status)}`}>
                        {result.status === 'timeout' ? 'Request timeout' : 'Request failed'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Network Tips */}
      <Card>
        <CardHeader>
          <CardTitle>{t('tools.pingTest.understandingPingResults')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Response Times:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• &lt;10ms: Excellent (Local network)</li>
                <li>• 10-50ms: Very Good (Same region)</li>
                <li>• 50-100ms: Good (Different region)</li>
                <li>• 100-200ms: Fair (International)</li>
                <li>• &gt;200ms: Poor (Slow connection)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Packet Loss:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• 0%: Perfect connection</li>
                <li>• 1-5%: Minor issues</li>
                <li>• 5-15%: Moderate problems</li>
                <li>• &gt;15%: Significant issues</li>
                <li>• 100%: No connectivity</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
